import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { SESSION_EXPIRED_EVENT } from './lib/sessionWatch';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  // True when a previously-authenticated session died (401 mid-use) — the
  // login page explains "you've been signed out" instead of appearing blankly (UX-72).
  const [sessionExpired, setSessionExpired] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return fetch('/api/me')
      .then(r => {
        if (r.status === 401) { setAuthed(false); setMe(null); return null; }
        setAuthed(true);
        setSessionExpired(false);
        return r.json().then(setMe);
      })
      .catch(() => { setAuthed(false); setMe(null); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Any business-API 401 while signed in means the session expired or was
  // invalidated (admin reset, token-version bump): swap to the login prompt.
  useEffect(() => {
    const onExpired = () => {
      setAuthed(a => {
        if (a) setSessionExpired(true);
        return false;
      });
      setMe(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const can = useCallback((moduleKey, verb) => {
    if (!me || !me.permissions) return false;
    const verbs = me.permissions[moduleKey];
    return Array.isArray(verbs) && verbs.includes(verb);
  }, [me]);

  return (
    <PermissionContext.Provider value={{ me, loading, authed, sessionExpired, can, reload }}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionProvider');
  return ctx;
}
